import { useState, useEffect } from 'react';
import {
  Box,
  Title,
  TextInput,
  FileInput,
  Button,
  Stack,
  Paper,
  Group,
  Image,
  Text,
  Alert,
  ColorSwatch,
  useMantineTheme,
} from '@mantine/core';
import { IconUpload, IconTrash, IconAlertCircle } from '@tabler/icons-react';
import { pb } from '../../lib/pocketbase';
import { convertToFavicon, processImageFile } from '../../utils/imageUtils';
import { RecordModel } from 'pocketbase';

// Valid Mantine colors
const COLORS = [
  { value: 'blue', label: 'Blue', hex: '#1c7ed6' },
  { value: 'red', label: 'Red', hex: '#e03131' },
  { value: 'green', label: 'Green', hex: '#37b24d' },
  { value: 'yellow', label: 'Yellow', hex: '#f08c00' },
  { value: 'orange', label: 'Orange', hex: '#e8590c' },
  { value: 'grape', label: 'Purple', hex: '#9c36b5' },
  { value: 'pink', label: 'Pink', hex: '#f06595' },
  { value: 'cyan', label: 'Cyan', hex: '#0c8599' },
  { value: 'teal', label: 'Teal', hex: '#099268' },
  { value: 'indigo', label: 'Indigo', hex: '#4263eb' },
];

interface SettingsConfig {
  title: string;
  primaryColor: string;
  deckblattImage?: string;
}

interface SettingsRecord extends RecordModel {
  config: SettingsConfig;
  logo?: string;
  favicon?: string;
  image?: string;
}

export function Settings() {
  const [settings, setSettings] = useState<SettingsRecord | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [faviconFile, setFaviconFile] = useState<File | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [shouldRemoveLogo, setShouldRemoveLogo] = useState(false);
  const [shouldRemoveFavicon, setShouldRemoveFavicon] = useState(false);
  const [shouldRemoveImage, setShouldRemoveImage] = useState(false);
  const [deckblattImageFile, setDeckblattImageFile] = useState<File | null>(null);
  const [shouldRemoveDeckblattImage, setShouldRemoveDeckblattImage] = useState(false);
  const theme = useMantineTheme();

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      // Try to get existing settings
      const records = await pb.collection('settings').getList(1, 1);
      
      if (records.items.length > 0) {
        // normalize record to ensure UI field names match DB field names
        const recordRaw: any = records.items[0];
        console.log('Fetched settings (raw):', recordRaw);

        // support both possible file-field names: "deckblatt" and "deckblattImage"
        if (recordRaw.deckblatt && !recordRaw.deckblattImage) {
          recordRaw.deckblattImage = recordRaw.deckblatt;
        }
        if (recordRaw.deckblattImage && !recordRaw.deckblatt) {
          recordRaw.deckblatt = recordRaw.deckblattImage;
        }

        const record = recordRaw as SettingsRecord;
        console.log('Normalized settings:', record);
        setSettings(record);
      } else {
        // Create default settings if none exist
        const defaultSettings = {
          config: {
            title: 'ASGS 2.1',
            primaryColor: 'blue'
          }
        };
        const newRecord = await pb.collection('settings').create(defaultSettings);
        console.log('Created default settings:', newRecord);
        setSettings(newRecord as SettingsRecord);
      }
    } catch (error: any) {
      console.error('Error fetching settings:', error);
      setError(error.message || 'Failed to fetch settings');
    }
  };

  const handleSave = async () => {
    if (!settings) return;

    setLoading(true);
    setError(null);

    try {
      const formData = new FormData();

      // Always include current config
      formData.append('config', JSON.stringify({
        title: settings.config.title,
        primaryColor: settings.config.primaryColor,
      }));

      // Handle logo
      if (logoFile) {
        try {
          const processedLogo = await processImageFile(logoFile);
          formData.append('logo', processedLogo);
        } catch (error) {
          console.error('Error processing logo:', error);
          setError('Failed to process logo file');
          setLoading(false);
          return;
        }
      } else if (shouldRemoveLogo) {
        formData.append('logo-', '');
      }

      // Handle favicon
      if (faviconFile) {
        try {
          const processedFavicon = await convertToFavicon(faviconFile);
          formData.append('favicon', processedFavicon);
        } catch (error) {
          console.error('Error processing favicon:', error);
          setError('Failed to process favicon file');
          setLoading(false);
          return;
        }
      } else if (shouldRemoveFavicon) {
        formData.append('favicon-', '');
      }

      // Handle login page background image
      if (imageFile) {
        formData.append('image', imageFile);
      } else if (shouldRemoveImage) {
        formData.append('image-', '');
      }

      // Handle Deckblatt-Felder — verarbeite wie bei Logo (resize/optimize), damit Upload genauso funktioniert
      if (deckblattImageFile) {
        try {
          const processedDeckblatt = await processImageFile(deckblattImageFile);
          formData.append('deckblatt', processedDeckblatt, deckblattImageFile.name);
        } catch (error) {
          console.error('Error processing deckblatt:', error);
          setError('Failed to process Deckblatt file');
          setLoading(false);
          return;
        }
      } else if (shouldRemoveDeckblattImage) {
        // PocketBase-Konvention: Feldname + '-' löscht Datei
        formData.append('deckblatt-', '');
      }

      console.log('Saving settings with FormData:', {
        config: settings.config,
        hasLogo: !!logoFile,
        hasFavicon: !!faviconFile,
        hasImage: !!imageFile,
        hasDeckblatt: !!deckblattImageFile,
        removeLogo: shouldRemoveLogo,
        removeFavicon: shouldRemoveFavicon,
        removeImage: shouldRemoveImage,
        removeDeckblatt: shouldRemoveDeckblattImage
      });

      // Debug FormData contents
      console.log('FormData entries:');
      const entries = Array.from(formData.entries());
      entries.forEach(([key, value]) => {
        console.log(key, value instanceof File ? `File: ${value.name} (${value.size} bytes)` : value);
      });

      // Update settings
      const updatedSettings = await pb.collection('settings').update(settings.id, formData);
      console.log('Updated settings:', updatedSettings);

      // Update favicon in browser
      if (updatedSettings.favicon) {
        const faviconUrl = pb.files.getURL(updatedSettings, updatedSettings.favicon);
        const link = document.querySelector("link[rel*='icon']") as HTMLLinkElement || document.createElement('link');
        link.type = 'image/x-icon';
        link.rel = 'shortcut icon';
        link.href = faviconUrl;
        document.getElementsByTagName('head')[0].appendChild(link);
      }

      // Reset file states
      setLogoFile(null);
      setFaviconFile(null);
      setImageFile(null);
      setDeckblattImageFile(null);
      setShouldRemoveLogo(false);
      setShouldRemoveFavicon(false);
      setShouldRemoveImage(false);
      setShouldRemoveDeckblattImage(false);

      // Reload page to apply changes
      window.location.reload();
    } catch (error: any) {
      console.error('Error saving settings:', error);
      setError(error.message || 'Failed to save settings');
    } finally {
      setLoading(false);
    }
  };

  if (!settings) {
    return (
      <Box p="md">
        <Paper shadow="xs" p="md" withBorder>
          <Text>Loading settings...</Text>
        </Paper>
      </Box>
    );
  }

  const getFileUrl = (filename: string) => {
    if (!settings || !filename) return '';
    return pb.files.getURL(settings, filename);
  };

  return (
    <Box p="md">
      <Paper shadow="xs" p="md" withBorder>
        <Title order={2} mb="lg">Einstellungen</Title>
        
        {error && (
          <Alert icon={<IconAlertCircle size={16} />} color="red" mb="md">
            {error}
          </Alert>
        )}

        <Stack gap="md">
          <TextInput
            label="Webapp Titel"
            value={settings.config.title}
            onChange={(e) => setSettings({
              ...settings,
              config: { ...settings.config, title: e.target.value }
            })}
          />
        
          <Box>
            <Text fw={500} size="sm" mb="xs">Primärfarbe</Text>
            <Group gap="xs">
              {COLORS.map((color) => (
                <Box 
                  key={color.value}
                  onClick={() => setSettings({
                    ...settings,
                    config: { ...settings.config, primaryColor: color.value }
                  })}
                  style={{ cursor: 'pointer' }}
                >
                  <ColorSwatch 
                    color={color.hex}
                    size={30}
                    style={{
                      border: settings.config.primaryColor === color.value 
                        ? `2px solid ${theme.colors.gray[7]}`
                        : undefined
                    }}
                  />
                  <Text size="xs" ta="center" mt={4}>
                    {color.label}
                  </Text>
                </Box>
              ))}
            </Group>
          </Box>
          <Box>
            <Text fw={500} size="sm" mb={4}>Deckblatt Bild</Text>
            <Group align="flex-start">
              {(settings.deckblatt || settings.deckblattImage) && !shouldRemoveDeckblattImage && !deckblattImageFile && (
                <Box pos="relative">
                  <Image
                    src={pb.files.getURL(settings, settings.deckblatt || settings.deckblattImage)}
                    width={200}
                    height={150}
                    fit="cover"
                  />
                  <Button 
                    color="red" 
                    size="xs" 
                    variant="light"
                    onClick={() => setShouldRemoveDeckblattImage(true)}
                    leftSection={<IconTrash size={14} />}
                    mt={5}
                  >
                    Entfernen
                  </Button>
                </Box>
              )}
              {deckblattImageFile && (
                <Box pos="relative">
                  <Image
                    src={URL.createObjectURL(deckblattImageFile)}
                    width={200}
                    height={150}
                    fit="cover"
                  />
                  <Button 
                    color="red" 
                    size="xs" 
                    variant="light"
                    onClick={() => setDeckblattImageFile(null)}
                    leftSection={<IconTrash size={14} />}
                    mt={5}
                  >
                    Entfernen
                  </Button>
                </Box>
              )}
              <FileInput
                placeholder="Deckblatt Bild hochladen"
                accept="image/*"
                leftSection={<IconUpload size={14} />}
                value={deckblattImageFile}
                onChange={(file) => {
                  setDeckblattImageFile(file);
                  setShouldRemoveDeckblattImage(false);
                }}
              />
            </Group>
          </Box>

          <Box>
            <Text fw={500} size="sm" mb={4}>Logo</Text>
            <Group align="flex-start">
              {settings.logo && !shouldRemoveLogo && !logoFile && (
                <Box pos="relative">
                  <Image
                    src={getFileUrl(settings.logo)}
                    width={100}
                    height={100}
                    fit="contain"
                  />
                  <Button 
                    color="red" 
                    size="xs" 
                    variant="light"
                    onClick={() => setShouldRemoveLogo(true)}
                    leftSection={<IconTrash size={14} />}
                    mt={5}
                  >
                    Remove
                  </Button>
                </Box>
              )}
              {logoFile && (
                <Box pos="relative">
                  <Image
                    src={URL.createObjectURL(logoFile)}
                    width={100}
                    height={100}
                    fit="contain"
                  />
                  <Button 
                    color="red" 
                    size="xs" 
                    variant="light"
                    onClick={() => setLogoFile(null)}
                    leftSection={<IconTrash size={14} />}
                    mt={5}
                  >
                    Remove
                  </Button>
                </Box>
              )}
              <FileInput
                placeholder="Logo hochladen"
                accept="image/*"
                leftSection={<IconUpload size={14} />}
                value={logoFile}
                onChange={(file) => {
                  setLogoFile(file);
                  setShouldRemoveLogo(false);
                }}
              />
            </Group>
          </Box>

          <Box>
            <Text fw={500} size="sm" mb={4}>Favicon</Text>
            <Group align="flex-start">
              {settings.favicon && !shouldRemoveFavicon && !faviconFile && (
                <Box pos="relative">
                  <Image
                    src={getFileUrl(settings.favicon)}
                    width={32}
                    height={32}
                    fit="contain"
                  />
                  <Button 
                    color="red" 
                    size="xs" 
                    variant="light"
                    onClick={() => setShouldRemoveFavicon(true)}
                    leftSection={<IconTrash size={14} />}
                    mt={5}
                  >
                    Remove
                  </Button>
                </Box>
              )}
              {faviconFile && (
                <Box pos="relative">
                  <Image
                    src={URL.createObjectURL(faviconFile)}
                    width={32}
                    height={32}
                    fit="contain"
                  />
                  <Button 
                    color="red" 
                    size="xs" 
                    variant="light"
                    onClick={() => setFaviconFile(null)}
                    leftSection={<IconTrash size={14} />}
                    mt={5}
                  >
                    Remove
                  </Button>
                </Box>
              )}
              <FileInput
                placeholder="Favicon hochladen"
                accept="image/*"
                leftSection={<IconUpload size={14} />}
                value={faviconFile}
                onChange={(file) => {
                  setFaviconFile(file);
                  setShouldRemoveFavicon(false);
                }}
              />
            </Group>
          </Box>

          <Box>
            <Text fw={500} size="sm" mb={4}>Login Hintergrundbild</Text>
            <Group align="flex-start">
              {settings.image && !shouldRemoveImage && !imageFile && (
                <Box pos="relative">
                  <Image
                    src={getFileUrl(settings.image)}
                    width={200}
                    height={150}
                    fit="cover"
                  />
                  <Button 
                    color="red" 
                    size="xs" 
                    variant="light"
                    onClick={() => setShouldRemoveImage(true)}
                    leftSection={<IconTrash size={14} />}
                    mt={5}
                  >
                    Remove
                  </Button>
                </Box>
              )}
              {imageFile && (
                <Box pos="relative">
                  <Image
                    src={URL.createObjectURL(imageFile)}
                    width={200}
                    height={150}
                    fit="cover"
                  />
                  <Button 
                    color="red" 
                    size="xs" 
                    variant="light"
                    onClick={() => setImageFile(null)}
                    leftSection={<IconTrash size={14} />}
                    mt={5}
                  >
                    Remove
                  </Button>
                </Box>
              )}
              <FileInput
                placeholder="Hintergrundbild hochladen"
                accept="image/*"
                leftSection={<IconUpload size={14} />}
                value={imageFile}
                onChange={(file) => {
                  setImageFile(file);
                  setShouldRemoveImage(false);
                }}
              />
            </Group>
            <Text size="xs" c="dimmed" mt={5}>
              Empfohlene Größe: mindestens 1200x800 Pixel für beste Qualität
            </Text>
          </Box>

          <Button
            onClick={handleSave}
            loading={loading}
          >
            Einstellungen speichern
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
}
